import React from 'react';
import FormInput from '../../../shared/components/ui/FormInput';
import { ActionParty, CaseStatus, toOptions, withBlank } from './legalCase';

/**
 * The court-case block shared by Create Task and Edit Court Case. Field names
 * sit under `prefix` so they never collide with the task's own. Mount it only
 * once `options.loaded` (see useLegalCaseOptions).
 */
const LegalCaseFields = ({ register, watch, options, userOptions, prefix = 'legalCase' }) => {
    const key = (name) => `${prefix}.${name}`;
    const caseStatus = watch(key('caseStatus'));
    const pendingFrom = watch(key('actionPendingFrom'));
    const escalation = watch(key('escalationNeeded'));

    // A plain call, not a component: a component declared in render would
    // remount its input on every keystroke.
    const field = (name, props) => (
        <FormInput register={register} register_key={key(name)} width={100} {...props} />
    );

    return (
        <>
            <div className="col-span-2 border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-800">Court case</h3>
                <p className="text-xs text-gray-500">The case status sets the task status.</p>
            </div>
            {field('caseType', {
                label: 'Case type',
                type: 'dropdown',
                options: withBlank(toOptions(options.caseTypes)),
                isRequired: true
            })}
            {field('court', { label: 'Court / Authority', type: 'text', isRequired: true })}
            {field('caseNumber', { label: 'Case no.', type: 'text' })}
            {field('opponent', { label: 'Party / Opponent', type: 'text' })}
            {field('advocate', { label: 'Advocate', type: 'text' })}
            {field('filingDate', { label: 'Filing date', type: 'date' })}
            {field('caseStatus', {
                label: 'Case status',
                type: 'dropdown',
                options: toOptions(options.caseStatuses),
                isRequired: true
            })}
            {field('currentStage', { label: 'Current stage', type: 'text', maxLength: 200 })}
            {field('riskLevel', {
                label: 'Risk level',
                type: 'dropdown',
                options: toOptions(options.riskLevels),
                isRequired: true
            })}
            {field('actionPendingFrom', {
                label: 'Action pending from',
                type: 'dropdown',
                options: withBlank(toOptions(options.actionParties), 'Nobody')
            })}
            {pendingFrom === ActionParty.INTERNAL &&
                (userOptions.loaded ? (
                    field('actionPendingUserId', {
                        label: 'Pending with',
                        type: 'dropdown',
                        options: withBlank(userOptions.options),
                        isRequired: true
                    })
                ) : (
                    <p className="self-end text-sm text-gray-500">Loading people…</p>
                ))}
            <div className="col-span-2">{field('nextAction', { label: 'Next action', type: 'text' })}</div>
            {field('exposureAmount', { label: 'Estimated exposure (₹)', type: 'number' })}
            {field('legalFeesPending', { label: 'Legal fees pending (₹)', type: 'number' })}
            <label className="col-span-2 flex items-center gap-2 text-gray-600">
                <input type="checkbox" className="h-4 w-4" {...register(key('escalationNeeded'))} />
                Escalation needed
            </label>
            {escalation && (
                <div className="col-span-2">
                    {field('escalationReason', { label: 'Escalation reason', type: 'text', isRequired: true })}
                </div>
            )}
            <div className="col-span-2">
                {field('latestRemarks', { label: 'Latest update / remarks', type: 'textarea' })}
            </div>
            <div className="col-span-2">
                {field('folderLink', { label: 'Document folder link', type: 'text', placeholder: 'https://' })}
            </div>
            {caseStatus === CaseStatus.CLOSED && (
                <div className="col-span-2">
                    {field('closingNote', { label: 'Closing note', type: 'textarea', isRequired: true })}
                </div>
            )}
        </>
    );
};

export default LegalCaseFields;
